import { useEffect, useRef } from "react";
import * as THREE from "three";
import { STLLoader } from "three-stdlib";
import { OrbitControls } from "three-stdlib";

// Renders an STL model from an ArrayBuffer with auto-rotate + orbit controls.
export default function STLViewer({ arrayBuffer }) {
  const mountRef = useRef(null);

  useEffect(() => {
    if (!arrayBuffer || !mountRef.current) return;
    const mount = mountRef.current;
    const width = mount.clientWidth;
    const height = mount.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#0A0E14");

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 2000);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    try {
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setSize(width, height);
      mount.appendChild(renderer.domElement);
    } catch (e) {
      return;
    }

    // Lights
    scene.add(new THREE.HemisphereLight(0xffffff, 0x1a1f2b, 1.1));
    const key = new THREE.DirectionalLight(0x00c8d7, 1.4);
    key.position.set(1, 1, 1);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xf5871f, 0.8);
    fill.position.set(-1, -0.5, -1);
    scene.add(fill);

    // Geometry
    let mesh;
    try {
      const geometry = new STLLoader().parse(arrayBuffer);
      geometry.computeVertexNormals();
      geometry.center();
      const material = new THREE.MeshStandardMaterial({
        color: 0xc9d4e0,
        metalness: 0.25,
        roughness: 0.55,
        flatShading: false,
      });
      mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);

      // Fit camera to model
      geometry.computeBoundingSphere();
      const r = geometry.boundingSphere.radius || 10;
      camera.position.set(0, r * 0.6, r * 2.6);
      camera.lookAt(0, 0, 0);
    } catch (e) {
      // parsing failed; leave empty scene
    }

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 2.2;

    let raf;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      controls.dispose();
      if (mesh) {
        mesh.geometry.dispose();
        mesh.material.dispose();
      }
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, [arrayBuffer]);

  return <div ref={mountRef} data-testid="stl-canvas" className="w-full h-full" />;
}
